import { IonApp, IonContent, IonLoading, IonSplitPane } from '@ionic/react';
import React, { useEffect, useMemo } from 'react';
import { useBreakpoint } from '../../utils/helpers/platformHelpers';
import { JobReportGuestMenu } from './JobReportGuestMenu';
import { JobInfoByIdPage } from '../job-info/JobInfoByIdPage';
import { useParams } from 'react-router';
import { ShareValidationFailedPage } from './ShareValidationFailedPage';
import { tryFetchLogInfo } from '../../redux/logInfo/thunk/tryFetchLogInfo';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../redux/types';
import { AnyAction } from 'redux';
import { selectLogInfo } from '../../redux/logInfo/selector';
import { TryFetchLogInfoStatus } from '../../redux/logInfo/types';
import { selectAuthUserInfo } from '../../redux/auth/selectors';
import { AppSocketContextForGuest } from './AppSocketContextForGuest';

enum Components {
  ION_LOADING,
  JOB_INFO_BY_ID_PAGE,
  SHARE_VALIDATION_FAILED_PAGE,
}

/**
 * User will be navigated to this page when security code is valid.
 *
 * It will display the menu on the left and will validate the share link before presenting
 * the user with the job report.
 */
export const JobReportGuestLayout: React.FC = () => {
  const isLargeBreakpoint = useBreakpoint('lg');

  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();

  const fetchResult = useSelector(selectLogInfo);
  const user = useSelector(selectAuthUserInfo);

  const { id: jobId } = useParams<{ id: string }>();
  useEffect(() => {
    dispatch(tryFetchLogInfo(jobId, user));
  }, [dispatch, jobId, user]);

  const whichComponent: Components = useMemo(() => {
    if (
      fetchResult.status === TryFetchLogInfoStatus.NotStartedYet ||
      fetchResult.status === TryFetchLogInfoStatus.InProgress
    ) {
      return Components.ION_LOADING;
    }

    if (fetchResult.status === TryFetchLogInfoStatus.Failed) {
      return Components.SHARE_VALIDATION_FAILED_PAGE;
    }

    return Components.JOB_INFO_BY_ID_PAGE;
  }, [fetchResult.status]);

  return (
    <IonApp>
      <AppSocketContextForGuest>
        <IonSplitPane contentId="main" when={isLargeBreakpoint}>
          <JobReportGuestMenu />
          <div id="main">
            <IonContent scrollX={false} scrollY={false}>
              {whichComponent === Components.ION_LOADING && <IonLoading isOpen={true} spinner="circles" />}
              {whichComponent === Components.SHARE_VALIDATION_FAILED_PAGE && (
                <ShareValidationFailedPage failureType={fetchResult.failureType} messageParams={{}} />
              )}
              {whichComponent === Components.JOB_INFO_BY_ID_PAGE && <JobInfoByIdPage />}
            </IonContent>
          </div>
        </IonSplitPane>
      </AppSocketContextForGuest>
    </IonApp>
  );
};
